import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../api/auth';
import { BridgeMark } from '../components/icons/Svg';
import { Button } from '../ui/Button';
import { Input, Label } from '../ui/Input';
import { cn } from '../lib/cn';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSent(true);
    } catch (ex) {
      // Endpoint always returns 202, but we still surface unexpected network
      // / server errors so the user knows to try again.
      setErr(ex?.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4 text-[hsl(var(--fg))]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl animate-brief-breathe"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--primary) / 0.14), transparent 70%)',
        }}
      />
      <form
        className={cn(
          'relative z-10 flex w-full max-w-md flex-col gap-5',
          'rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-elev-3'
        )}
        onSubmit={submit}
      >
        <div className="flex items-center gap-3">
          <BridgeMark size={36} />
          <h1 className="m-0 text-xl font-semibold tracking-tight">Reset password</h1>
        </div>

        {sent ? (
          <>
            <p className="text-sm text-[hsl(var(--fg-muted))]">
              If that email matches an active account, we've sent a reset link
              good for 30 minutes. Check your inbox (and spam folder).
            </p>
            <Button asChild variant="ghost" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </>
        ) : (
          <>
            <p className="text-sm text-[hsl(var(--fg-muted))]">
              Enter your email. We'll send a single-use link to set a new
              password.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
            </div>
            {err && (
              <div
                role="alert"
                className="rounded-lg border border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.1)] px-3 py-2 text-sm"
              >
                {err}
              </div>
            )}
            <Button type="submit" disabled={busy || !email} className="w-full">
              {busy ? 'Sending' : 'Send reset link'}
            </Button>
            <Link
              to="/login"
              className="text-center text-xs text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]"
            >
              Back to sign in
            </Link>
          </>
        )}
      </form>
    </div>
  );
}
